import React from 'react';
import { useAppStore } from '@/store';
import { selectGradeImpact, selectCourseBudget } from '@/store/selectors';
import { computePassFailStatus } from '@abs/shared-logic';
import { CollapsiblePanel, EmptyState } from '../shared';

export interface GradeImpactPanelProps {
  courseId: string;
}

export function GradeImpactPanel({ courseId }: GradeImpactPanelProps): React.ReactElement {
  const course = useAppStore((s) => s.courses.find((c) => c.id === courseId));
  const budget = useAppStore((s) => selectCourseBudget(s, courseId));
  const impact = useAppStore((s) => selectGradeImpact(s, courseId));

  // Pass/Fail-aware card (TODO-06)
  if (course?.isPassFail) {
    const pf = computePassFailStatus(true, course.passThreshold ?? 70, budget?.attendancePercentage ?? 100);
    return (
      <div className="grade-impact-panel">
        <div className="grade-impact-panel__current">
          <h3>Pass / Fail Status</h3>
          {pf && (
            <>
              <div className="grade-impact-panel__grade">
                <span className="grade-impact-panel__letter">{pf.isPassing ? 'PASS' : 'FAIL'}</span>
                <span className="grade-impact-panel__points">{pf.currentPercentage.toFixed(1)}%</span>
              </div>
              <p className="grade-impact-panel__penalty">
                Margin <strong>{pf.margin > 0 ? '+' : ''}{pf.margin}%</strong> above {pf.threshold}% threshold.
              </p>
              <p className="stat-caption">Pass/fail standing is driven by your attendance percentage.</p>
            </>
          )}
        </div>

        {impact && (
          <CollapsiblePanel title="Penalty Audit Trail" defaultOpen={false}>
            <div className="grade-impact-panel__audit">
              {impact.auditTrail.map((step) => (
                <div key={step.step} className="audit-step">
                  <div className="audit-step__header">
                    <span className="audit-step__number">Step {step.step}</span>
                    <span className="audit-step__label">{step.label}</span>
                  </div>
                  <div className="audit-step__body">
                    <div className="audit-step__row">
                      <span className="audit-step__field">Input:</span>
                      <code>{step.input}</code>
                    </div>
                    <div className="audit-step__row">
                      <span className="audit-step__field">Formula:</span>
                      <em>{step.formula}</em>
                    </div>
                    <div className="audit-step__row">
                      <span className="audit-step__field">Output:</span>
                      <code>{step.output}</code>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CollapsiblePanel>
        )}
      </div>
    );
  }

  if (!impact) {
    return (
      <EmptyState
        title="No grade impact data"
        description="Set your current base grade in course settings to see grade impact projections."
      />
    );
  }

  return (
    <div className="grade-impact-panel">
      <div className="grade-impact-panel__current">
        <h3>Current Grade</h3>
        <div className="grade-impact-panel__grade">
          <span className="grade-impact-panel__letter">{impact.currentGrade}</span>
          <span className="grade-impact-panel__points">{impact.currentGradePoints.toFixed(1)}%</span>
        </div>
        <p className="grade-impact-panel__penalty">
          Penalty applied: <strong>−{impact.penaltyApplied.toFixed(1)} points</strong>
        </p>
        <p className="stat-caption">Grade after all active penalty tiers are deducted from your base grade.</p>
      </div>

      <div className="grade-impact-panel__next">
        <h4>If You Miss 1 More</h4>
        <div className="grade-impact-panel__grade">
          <span className="grade-impact-panel__letter">{impact.nextAbsenceGrade}</span>
          <span className="grade-impact-panel__points">{impact.nextAbsencePoints.toFixed(1)}%</span>
        </div>
        <p className="stat-caption">Projected grade if you incur one additional penalty absence.</p>
      </div>

      <div className="grade-impact-panel__ratio">
        <span>Penalty per absence: <strong>{impact.penaltyPerAbsence.toFixed(1)} points</strong></span>
        <p className="stat-caption">Average grade points lost per penalty absence incurred so far.</p>
      </div>

      <CollapsiblePanel title="Penalty Audit Trail" defaultOpen={false}>
        <div className="grade-impact-panel__audit">
          {impact.auditTrail.map((step) => (
            <div key={step.step} className="audit-step">
              <div className="audit-step__header">
                <span className="audit-step__number">Step {step.step}</span>
                <span className="audit-step__label">{step.label}</span>
              </div>
              <div className="audit-step__body">
                <div className="audit-step__row">
                  <span className="audit-step__field">Input:</span>
                  <code>{step.input}</code>
                </div>
                <div className="audit-step__row">
                  <span className="audit-step__field">Formula:</span>
                  <em>{step.formula}</em>
                </div>
                <div className="audit-step__row">
                  <span className="audit-step__field">Output:</span>
                  <code>{step.output}</code>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CollapsiblePanel>
    </div>
  );
}
